import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../../../components/CustomDropdown";
import Sidebar from "../../../components/Sidebar";
import ProfileInfoController from "../../../controller/profileInfoController";
import {
  KycStatus,
  UserDocumentType,
  UserDocumentSubType,
} from "../../../controller/profileInfoController";
import { fetchDocumentImage, getHomePageInfos } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import dropdownOptions from "../../../data/dropdownOptions.json";
import "../../css/ProfileInformation.css";

const {
  employmentStatusOptions,
  employmentIndustryOptions,
  educationLevelOptions,
  sourceOfWealthOptions,
  netWorthOptions,
  annualIncomeOptions,
  annualDepositOptions,
  experienceOptions,
  opsPaceOptions,
} = dropdownOptions;

function findOption(options, value) {
  if (value == null) return null;
  return (
    options.find(
      (o) =>
        o.value === value ||
        o.value === Number(value) ||
        String(o.value) === String(value)
    ) || null
  );
}

export default function UserRegulation() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [activeSection, setActiveSection] = useState("employment");

  // Collapsible sections (accordion)
  const [expandedSections, setExpandedSections] = useState({
    employment: true,
    income: false,
    experience: false,
    activity: false,
  });

  // Regulation fields
  const [taxId, setTaxId] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState(null);
  const [employmentIndustry, setEmploymentIndustry] = useState(null);
  const [educationLevel, setEducationLevel] = useState(null);
  const [sourceOfWealth, setSourceOfWealth] = useState(null);
  const [netWorth, setNetWorth] = useState(null);
  const [annualIncome, setAnnualIncome] = useState(null);
  const [annualDeposit, setAnnualDeposit] = useState(null);
  const [experienceForex, setExperienceForex] = useState(null);
  const [experienceStocks, setExperienceStocks] = useState(null);
  const [experienceFutures, setExperienceFutures] = useState(null);
  const [experienceCfd, setExperienceCfd] = useState(null);
  const [opsPace, setOpsPace] = useState(null);
  const [avgTxAmount, setAvgTxAmount] = useState("");
  const [expectedAnnualActivity, setExpectedAnnualActivity] = useState("");

  const [errors, setErrors] = useState({});

  const kycStatus = useMemo(() => {
    const info =
      profileData?.raw?.user_information ||
      profileData?.raw?.USER_INFORMATION ||
      {};
    return (
      info.IS_KYC_VERIFIED ?? info.isKycVerified ?? info.is_kyc_verified ?? 0
    );
  }, [profileData]);

  const canEdit =
    kycStatus !== KycStatus.VERIFIED &&
    kycStatus !== KycStatus.VERIFIED_NOT_CHECKED;

  const kycComments = useMemo(() => {
    const info =
      profileData?.raw?.user_information ||
      profileData?.raw?.USER_INFORMATION ||
      {};
    return info.COMMENTS ?? info.comments ?? "";
  }, [profileData]);

  const kycStatusText = useMemo(() => {
    if (
      kycStatus === KycStatus.VERIFIED ||
      kycStatus === KycStatus.VERIFIED_NOT_CHECKED
    )
      return t("verified", { ns: "mobile" });
    return t("not_verified", { ns: "mobile" });
  }, [kycStatus, t]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const populateFields = useCallback((data) => {
    setTaxId(data.taxId || "");
    setEmploymentStatus(findOption(employmentStatusOptions, data.employmentStatus));
    setEmploymentIndustry(findOption(employmentIndustryOptions, data.employmentIndustry));
    setEducationLevel(findOption(educationLevelOptions, data.educationLevel));
    setSourceOfWealth(findOption(sourceOfWealthOptions, data.sourceOfWealth));
    setNetWorth(findOption(netWorthOptions, data.netWorth));
    setAnnualIncome(findOption(annualIncomeOptions, data.annualIncome));
    setAnnualDeposit(findOption(annualDepositOptions, data.annualDeposit));
    setExperienceForex(findOption(experienceOptions, data.experienceForex));
    setExperienceStocks(findOption(experienceOptions, data.experienceStocks));
    setExperienceFutures(findOption(experienceOptions, data.experienceFutures));
    setExperienceCfd(findOption(experienceOptions, data.experienceCfd));
    setOpsPace(findOption(opsPaceOptions, data.opsPace));
    setAvgTxAmount(data.avgTxAmount != null ? String(data.avgTxAmount) : "");
    setExpectedAnnualActivity(data.expectedAnnualActivity != null ? String(data.expectedAnnualActivity) : "");
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let userDataFromStorage = null;
      try {
        const storedUserData = sessionStorage.getItem("userData");
        const storedTimestamp = sessionStorage.getItem("userDataTimestamp");
        if (storedUserData && storedTimestamp) {
          const age = Date.now() - parseInt(storedTimestamp, 10);
          if (age < 5 * 60 * 1000) {
            userDataFromStorage = JSON.parse(storedUserData);
            setUser(userDataFromStorage);
          }
        }
      } catch (_) {}

      if (!userDataFromStorage) {
        try {
          const homeResp = await getHomePageInfos();
          const homeData = homeResp?.decryptedData || homeResp?.data;
          if (
            homeData?.CODE === AppConstants.responseOk &&
            homeData?.DATA
          ) {
            const ud = homeData.DATA;
            setUser(ud);
            try {
              sessionStorage.setItem("userData", JSON.stringify(ud));
              sessionStorage.setItem("userDataTimestamp", String(Date.now()));
            } catch (_) {}
          }
        } catch (_) {}
      }

      const result = await ProfileInfoController.getProfileInfo({ withDoc: 0 });
      if (result.success && result.data) {
        setProfileData(result.data);
        populateFields(result.data);
      } else {
        setError(result.error || "Failed to load regulation data");
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [populateFields]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobileView) return;
    setExpandedSections({
      employment: activeSection === "employment",
      income: activeSection === "income",
      experience: activeSection === "experience",
      activity: activeSection === "activity",
    });
  }, [activeSection, isMobileView]);

  useEffect(() => {
    let cancelled = false;
    const loadHeaderImage = async () => {
      try {
        if (!profileData?.raw) {
          setAvatarUrl(null);
          return;
        }
        const docInfo = ProfileInfoController.extractDocumentUrls(profileData.raw);
        const imagePath = docInfo.profile || null;
        if (!imagePath) {
          setAvatarUrl(null);
          return;
        }
        const resolved = await fetchDocumentImage(imagePath);
        if (!cancelled) {
          setAvatarUrl(resolved || null);
        }
      } catch (_) {
        if (!cancelled) setAvatarUrl(null);
      }
    };
    loadHeaderImage();
    return () => {
      cancelled = true;
    };
  }, [profileData]);

  // ---------- TOGGLE SECTION (accordion) ----------
  const toggleSection = (key) => {
    if (!isMobileView) {
      setActiveSection(key);
      return;
    }
    setExpandedSections((prev) => {
      const isOpen = prev[key];
      const allClosed = { employment: false, income: false, experience: false, activity: false };
      return isOpen ? allClosed : { ...allClosed, [key]: true };
    });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setErrors({});
    if (profileData) {
      populateFields(profileData);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!taxId || taxId.trim() === "") newErrors.taxId = "Tax ID is required";
    if (!employmentStatus) newErrors.employmentStatus = "Employment Status is required";
    if (!employmentIndustry) newErrors.employmentIndustry = "Employment Industry is required";
    if (!educationLevel) newErrors.educationLevel = "Education Level is required";
    if (!sourceOfWealth) newErrors.sourceOfWealth = "Source of Wealth is required";
    if (!netWorth) newErrors.netWorth = "Net Worth is required";
    if (!annualIncome) newErrors.annualIncome = "Annual Income is required";
    if (!annualDeposit) newErrors.annualDeposit = "Annual Deposit is required";
    if (!experienceForex) newErrors.experienceForex = "Forex Experience is required";
    if (!experienceStocks) newErrors.experienceStocks = "Stocks Experience is required";
    if (!experienceFutures) newErrors.experienceFutures = "Futures Experience is required";
    if (!experienceCfd) newErrors.experienceCfd = "CFD Experience is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setError(null);

    try {
      const userId = profileData?.id || profileData?.raw?.ID;

      // Start from the existing raw regulation data (preserves USER_ID, CREATED_AT, UPDATED_AT, etc.)
      // then overlay the user's edits — matches Flutter's _userData!.userRegulation?.toJson()
      const rawReg =
        profileData?.raw?.user_regulation ||
        profileData?.raw?.USER_REGULATION ||
        profileData?.raw?.userRegulation ||
        {};
      const userRegulation = {
        ...rawReg,
        USER_ID: userId,
        TAX_ID: taxId.trim(),
        EMPLOYMENT_STATUS: employmentStatus?.value ?? null,
        EMPLOYMENT_INDUSTRY: employmentIndustry?.value ?? null,
        EDUCATION_LEVEL: educationLevel?.value ?? null,
        SOURCE_OF_WEALTH: sourceOfWealth?.value ?? null,
        NET_WORTH: netWorth?.value ?? null,
        ANNUAL_INCOME: annualIncome?.value ?? null,
        ANNUAL_DEPOSIT: annualDeposit?.value ?? null,
        EXPERIENCE_FOREX: experienceForex?.value ?? null,
        EXPERIENCE_STOCKS: experienceStocks?.value ?? null,
        EXPERIENCE_FUTURES: experienceFutures?.value ?? null,
        EXPERIENCE_CFD: experienceCfd?.value ?? null,
        OPS_PACE: opsPace?.value ?? rawReg.OPS_PACE ?? null,
        AVG_TX_AMOUNT: avgTxAmount ? parseFloat(avgTxAmount) : (rawReg.AVG_TX_AMOUNT ?? null),
        EXPECTED_ANNUAL_ACTIVITY: expectedAnnualActivity ? parseFloat(expectedAnnualActivity) : (rawReg.EXPECTED_ANNUAL_ACTIVITY ?? null),
      };

      const rawInfo =
        profileData?.raw?.user_information ||
        profileData?.raw?.USER_INFORMATION ||
        {};
      const userInformation = { ...rawInfo };

      // Fetch document data (withDoc: 1) needed for the save request
      // The backend requires USER_DOCUMENTS and related fields alongside regulation data
      let userDocuments = [];
      let selectedDocSubTypes = {};
      try {
        const docResult = await ProfileInfoController.getProfileInfo({ withDoc: 1 });
        if (docResult.success && docResult.data?.raw) {
          const docInfo = ProfileInfoController.extractDocumentUrls(docResult.data.raw);
          userDocuments = docInfo.rawDocuments || [];
          selectedDocSubTypes = {
            [UserDocumentType.PROOF_OF_IDENTITY]: docInfo.identityDocSubType ?? null,
            [UserDocumentType.PROOF_OF_ADDRESS]: docInfo.addressDocSubType ?? null,
          };
        }
      } catch (e) {
        console.error("[UserRegulation] Failed to fetch doc data:", e);
      }

      const result = await ProfileInfoController.saveRegulation({
        userId,
        userInformation,
        userRegulation,
        userDocuments,
        selectedDocSubTypes,
      });

      if (result.success) {
        setSuccess("Regulation information saved successfully!");
        setIsEditMode(false);
        await fetchData();
      } else {
        setError(result.error || "Failed to save regulation data");
      }
    } catch (err) {
      setError(err.message || "An error occurred while saving");
    } finally {
      setSaving(false);
    }
  };

  const clearError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: null }));
    }
  };

  const isVerified =
    kycStatus === KycStatus.VERIFIED ||
    kycStatus === KycStatus.VERIFIED_NOT_CHECKED;
  const resolvedFirstName =
    profileData?.firstName ||
    user?.F_NAME ||
    user?.f_name ||
    user?.first_name ||
    "";
  const resolvedLastName =
    profileData?.lastName ||
    user?.L_NAME ||
    user?.l_name ||
    user?.last_name ||
    "";
  const displayName =
    [resolvedFirstName, resolvedLastName].filter(Boolean).join(" ").trim() ||
    "User";

  // ---------- HELPERS ----------
  const isDisabled = !isEditMode || !canEdit;

  const renderField = (label, icon, value, onChange, opts = {}) => {
    const { type = "text", placeholder, required: isRequired, step, min, errorKey } = opts;
    return (
      <div className="profile-form-group">
        <label className="profile-form-label">
          {label}
          {isRequired && <span className="profile-required-star">*</span>}
        </label>
        <div className="profile-input-wrapper">
          <span className="profile-input-icon">
            <i className={`fa-solid ${icon}`}></i>
          </span>
          <input
            type={type}
            step={step}
            min={min}
            className={`profile-input ${errorKey && errors[errorKey] ? "profile-input--error" : ""}`}
            placeholder={placeholder || label}
            value={value}
            disabled={isDisabled || saving}
            readOnly={isDisabled}
            onChange={(e) => {
              onChange(e.target.value);
              if (errorKey) clearError(errorKey);
            }}
          />
        </div>
        {errorKey && errors[errorKey] && (
          <span style={{ color: "#E74A3B", fontSize: 12, marginTop: 2 }}>
            {errors[errorKey]}
          </span>
        )}
      </div>
    );
  };

  const renderDropdown = (label, options, value, onChange, opts = {}) => {
    const { required: isRequired, errorKey } = opts;
    return (
      <div className="profile-form-group">
        <label className="profile-form-label">
          {label}
          {isRequired && <span className="profile-required-star">*</span>}
        </label>
        <CustomDropdown
          options={options}
          value={value}
          onChange={(v) => {
            onChange(v);
            if (errorKey) clearError(errorKey);
          }}
          placeholder={label}
          className={`profile-dropdown ${errorKey && errors[errorKey] ? "profile-input--error" : ""}`}
          disabled={isDisabled || saving}
        />
        {errorKey && errors[errorKey] && (
          <span style={{ color: "#E74A3B", fontSize: 12, marginTop: 2 }}>
            {errors[errorKey]}
          </span>
        )}
      </div>
    );
  };

  // ---------- SECTION HEADER ----------
  const renderSectionHeader = (title, sectionKey) => (
    <div
      className="profile-section-header"
      onClick={() => toggleSection(sectionKey)}
    >
      <h3 className="profile-section-title profile-section-title--collapsible">
        {title}
      </h3>
      <i
        className={`fa-solid ${
          expandedSections[sectionKey] ? "fa-chevron-up" : "fa-chevron-down"
        } profile-section-chevron`}
      ></i>
    </div>
  );

  // ---------- SECTION 1: Financial & Employment ----------
  const renderEmploymentSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("financial_employment_details", { ns: "mobile" }), "employment")}
      {expandedSections.employment && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderField(t("tax_id", { ns: "mobile" }), "fa-id-card", taxId, setTaxId, {
              required: true,
              errorKey: "taxId",
              placeholder: t("enter_your_tax_id", { ns: "mobile" }),
            })}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("employment_status", { ns: "mobile" }),
              employmentStatusOptions,
              employmentStatus,
              setEmploymentStatus,
              { required: true, errorKey: "employmentStatus" }
            )}
            {renderDropdown(
              t("employment_industry", { ns: "mobile" }),
              employmentIndustryOptions,
              employmentIndustry,
              setEmploymentIndustry,
              { required: true, errorKey: "employmentIndustry" }
            )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("education_level", { ns: "mobile" }),
              educationLevelOptions,
              educationLevel,
              setEducationLevel,
              { required: true, errorKey: "educationLevel" }
            )}
            {renderDropdown(
              t("source_of_wealth", { ns: "mobile" }),
              sourceOfWealthOptions,
              sourceOfWealth,
              setSourceOfWealth,
              { required: true, errorKey: "sourceOfWealth" }
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- SECTION 2: Net Worth & Income ----------
  const renderIncomeSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("net_worth_income", { ns: "mobile" }), "income")}
      {expandedSections.income && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderDropdown(
              t("net_worth", { ns: "mobile" }),
              netWorthOptions,
              netWorth,
              setNetWorth,
              { required: true, errorKey: "netWorth" }
            )}
            {renderDropdown(
              t("annual_income", { ns: "mobile" }),
              annualIncomeOptions,
              annualIncome,
              setAnnualIncome,
              { required: true, errorKey: "annualIncome" }
            )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("annual_deposit", { ns: "mobile" }),
              annualDepositOptions,
              annualDeposit,
              setAnnualDeposit,
              { required: true, errorKey: "annualDeposit" }
            )}
            {renderDropdown(
              t("operations_pace", { ns: "mobile" }),
              opsPaceOptions,
              opsPace,
              setOpsPace,
              {}
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- SECTION 3: Trading Experience ----------
  const renderExperienceSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("trading_experience", { ns: "mobile" }), "experience")}
      {expandedSections.experience && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderDropdown(
              t("forex_experience", { ns: "mobile" }),
              experienceOptions,
              experienceForex,
              setExperienceForex,
              { required: true, errorKey: "experienceForex" }
            )}
            {renderDropdown(
              t("stocks_experience", { ns: "mobile" }),
              experienceOptions,
              experienceStocks,
              setExperienceStocks,
              { required: true, errorKey: "experienceStocks" }
            )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("futures_experience", { ns: "mobile" }),
              experienceOptions,
              experienceFutures,
              setExperienceFutures,
              { required: true, errorKey: "experienceFutures" }
            )}
            {renderDropdown(
              t("cfd_experience", { ns: "mobile" }),
              experienceOptions,
              experienceCfd,
              setExperienceCfd,
              { required: true, errorKey: "experienceCfd" }
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- SECTION 4: Transaction ----------
  const renderActivitySection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("transaction", { ns: "mobile" }), "activity")}
      {expandedSections.activity && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderField(
              t("average_transaction_amount", { ns: "mobile" }),
              "fa-dollar-sign",
              avgTxAmount,
              setAvgTxAmount,
              {
                type: "number",
                step: "0.01",
                min: "0",
                placeholder: t("enter_average_transaction_amount", { ns: "mobile" }),
              }
            )}
            {renderField(
              t("expected_annual_activity", { ns: "mobile" }),
              "fa-chart-line",
              expectedAnnualActivity,
              setExpectedAnnualActivity,
              {
                type: "number",
                step: "0.01",
                min: "0",
                placeholder: t("enter_expected_annual_activity", { ns: "mobile" }),
              }
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- RENDER ----------
  return (
    <div className="profile-information-page user-regulation-page">
      <Sidebar user={user} />
      <div className="profile-information-content">
        {error && (
          <div className="profile-error-message">
            <strong>{t("error", { ns: "mobile" })}:</strong> {error}
          </div>
        )}
        {success && (
          <div className="profile-success-message">
            <strong>{t("success", { ns: "mobile" })}:</strong> {success}
          </div>
        )}
        {loading && (
          <div className="profile-loading-message">
            {t("loading_regulation_data", { ns: "mobile" })}
          </div>
        )}

        {!loading && profileData && (
          <div className="profile-information-container profile-information-container--plain" style={{ position: "relative" }}>
            {/* Edit / Cancel Button */}
            {!loading && canEdit && !isEditMode && (
              <button
                type="button"
                className="profile-edit-btn profile-edit-btn--card"
                onClick={() => setIsEditMode(true)}
              >
                <i className="fa-solid fa-pen-to-square"></i>
                {t("edit", { ns: "mobile" })}
              </button>
            )}
            {isEditMode && (
              <button
                type="button"
                className="profile-cancel-edit-btn profile-cancel-edit-btn--card"
                onClick={handleCancelEdit}
              >
                <i className="fa-solid fa-xmark"></i>
                {t("cancel", { ns: "mobile" })}
              </button>
            )}

            {/* Header */}
            <div className="profile-avatar-section">
              <div className="profile-avatar-circle">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="profile-avatar-img"
                    onError={(e) => {
                      e.target.style.display = "none";
                      if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="profile-avatar-fallback"
                  style={{ display: avatarUrl ? "none" : "flex" }}
                >
                  <i className="fa-solid fa-user"></i>
                </div>
              </div>
              <div className="profile-avatar-info">
                <span className="profile-avatar-name">{displayName}</span>
                <span
                  className={`profile-avatar-status ${
                    isVerified
                      ? "profile-avatar-status--verified"
                      : "profile-avatar-status--unverified"
                  }`}
                >
                  <i
                    className={`fa-solid ${
                      isVerified ? "fa-circle-check" : "fa-circle-exclamation"
                    }`}
                  ></i>
                  {kycStatusText}
                </span>
              </div>
            </div>

            {/* KYC Comments */}
            {kycStatus !== KycStatus.VERIFIED && kycComments && (
              <div className="profile-kyc-comments">{kycComments}</div>
            )}

            {/* Desktop horizontal section switcher */}
            {!isMobileView && (
              <div className="profile-sections-swiper" role="tablist" aria-label="Regulation sections">
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "employment" ? "active" : ""}`}
                  onClick={() => setActiveSection("employment")}
                >
                  {t("financial_employment_details", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "income" ? "active" : ""}`}
                  onClick={() => setActiveSection("income")}
                >
                  {t("net_worth_income", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "experience" ? "active" : ""}`}
                  onClick={() => setActiveSection("experience")}
                >
                  {t("trading_experience", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "activity" ? "active" : ""}`}
                  onClick={() => setActiveSection("activity")}
                >
                  {t("transaction", { ns: "mobile" })}
                </button>
              </div>
            )}

            {/* Sections: mobile keeps collapsible, desktop shows active section */}
            {isMobileView ? (
              <>
                {renderEmploymentSection()}
                {renderIncomeSection()}
                {renderExperienceSection()}
                {renderActivitySection()}
              </>
            ) : (
              <>
                {activeSection === "employment" && renderEmploymentSection()}
                {activeSection === "income" && renderIncomeSection()}
                {activeSection === "experience" && renderExperienceSection()}
                {activeSection === "activity" && renderActivitySection()}
              </>
            )}

            {/* Save Button */}
            {isEditMode && (
              <div className="profile-nav-buttons profile-nav-buttons--single">
                <button
                  className="profile-btn-save"
                  onClick={handleSave}
                  disabled={saving}
                  type="button"
                >
                  {saving ? (
                    t("saving", { ns: "mobile" }) + "..."
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i>
                      {t("save_changes", { ns: "mobile" })}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
